import { T, useT } from "@transifex/react";
import { router } from "expo-router";

import { formatPhoneNumber, getPhoneDigits } from "@/utils/formatPhoneNumber";
import { signUpSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Pressable } from "react-native";
import type { z } from "zod";

import ArrowRight from "@/assets/icons/ArrowRight";
import AppButton from "@/components/shared/AppButton";
import PasswordInput from "@/components/shared/PasswordInput";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";

type SignUpSchemaType = z.infer<typeof signUpSchema>;

interface SignUpFormProps {
  phoneErrorMessage?: string;
  onSubmit: (data: SignUpSchemaType) => void;
  isLoading: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ phoneErrorMessage, onSubmit, isLoading }) => {
  const t = useT();
  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<SignUpSchemaType>({
    resolver: zodResolver(signUpSchema),
  });

  useEffect(() => {
    if (phoneErrorMessage) {
      setError("phone", { message: phoneErrorMessage });
    }
  }, [phoneErrorMessage, setError]);

  return (
    <VStack className="gap-[32px]">
      <VStack className="gap-[20px]">
        <FormControl isInvalid={!!errors.email} key="email-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Email" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.email ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("Enter Your Email Address")}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={() => clearErrors("email")}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="next"
                  autoComplete="email"
                  accessibilityLabel={t("Email Address")}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.email?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isInvalid={!!errors.first} key="first-name-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="First Name" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="first"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.first ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("Enter Your First Name")}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={() => clearErrors("first")}
                  accessibilityLabel={t("First Name")}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.first?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isInvalid={!!errors.last} key="last-name-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Last Name" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="last"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.last ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("Enter Your Last Name")}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={() => clearErrors("last")}
                  accessibilityLabel={t("Last Name")}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.last?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isInvalid={!!errors.phone} key="phone-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Phone Number" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.phone ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("+1 (xxx) xxx-xxxx")}
                  value={typeof value === "string" ? formatPhoneNumber(value) : ""}
                  onChangeText={(text: string) => onChange(getPhoneDigits(text))}
                  onBlur={onBlur}
                  onFocus={() => clearErrors("phone")}
                  keyboardType="phone-pad"
                  returnKeyType="next"
                  accessibilityLabel={t("Phone Number")}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.phone?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isInvalid={!!errors.password} key="password-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onFocus={() => clearErrors("password")}
                error={errors.password?.message}
                className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.password?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isInvalid={!!errors.confirmPassword} key="confirm-password-signup-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Confirm Password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onFocus={() => clearErrors("confirmPassword")}
                error={errors.confirmPassword?.message}
                className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.confirmPassword?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      </VStack>
      <AppButton title={t("Sign up")} onPress={handleSubmit(onSubmit)} loading={isLoading} icon={ArrowRight} iconPosition="after" />
      <HStack className=" items-center justify-center">
        <Text className="text-center text-sm text-typography-900">
          <T _str="Already have an account?" />{" "}
        </Text>
        <Pressable className="active:opacity-70 inline" onPress={() => router.push("/auth/sign-in")}>
          <Text className="text-typography-900 font-bold underline text-sm">
            <T _str="Sign-in." />
          </Text>
        </Pressable>
      </HStack>
    </VStack>
  );
};

export default SignUpForm;

export const SignUpFormSkeleton = () => {
  return (
    <VStack className="gap-[32px]">
      <SkeletonText className="h-[20px] w-1/2 self-center" />
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <VStack className="gap-[10px]">
        <SkeletonText _lines={1} className="h-4 w-1/3" />
        <Skeleton variant="rounded" className="h-12 w-full" />
      </VStack>
      <Skeleton variant="rounded" className="h-[52px] w-full" />
    </VStack>
  );
};
